import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { translateError, type VixioError } from "./errors";

// ---------------------------------------------------------------- papéis

export type Role = "owner" | "admin" | "manager" | "member" | "partner" | "client" | "viewer";

export const ROLES: ReadonlyArray<{ role: Role; rank: number; label: string }> = [
  { role: "owner", rank: 100, label: "Proprietário" },
  { role: "admin", rank: 80, label: "Administrador" },
  { role: "manager", rank: 60, label: "Gestor" },
  { role: "member", rank: 40, label: "Equipe" },
  { role: "partner", rank: 30, label: "Parceiro" },
  { role: "client", rank: 20, label: "Cliente" },
  { role: "viewer", rank: 10, label: "Leitura" },
];

export function roleRank(role: Role | string | null | undefined): number {
  return ROLES.find((r) => r.role === role)?.rank ?? 0;
}
export function roleLabel(role: Role | string | null | undefined): string {
  return ROLES.find((r) => r.role === role)?.label ?? String(role ?? "");
}

// ---------------------------------------------------------------- organização

export interface OrgInfo {
  id: string;
  name: string;
  slug: string;
  kind: string;
  role: Role;
  rank: number;
  brand: Record<string, unknown>;
}

export interface ModuleStatus {
  name: string;
  title: string;
  featureFlag: string | null;
  /** Aplicado no banco (core.installed_modules). */
  installed: boolean;
  /** Ligado na organização atual (feature flag; ausente = ligado). */
  enabled: boolean;
  /** Tem telas no painel (modules/<nome>/ui). */
  hasUi: boolean;
}

export interface SessionInfo {
  userId: string;
  email: string | null;
  org: OrgInfo | null;
  orgs: OrgInfo[];
  isPlatformAdmin: boolean;
  /** Nível de verificação da sessão ("aal1" | "aal2"). */
  aal: string;
  switchOrg: (id: string) => void;
  /** Módulos conhecidos pelo painel e pelo banco, com o estado na organização atual. */
  modules: ModuleStatus[];
  /** Recarrega organizações, módulos e flags (depois de criar organização ou mudar flag). */
  refresh: () => Promise<void>;
}

const SessionCtx = createContext<SessionInfo | null>(null);

export function SessionProvider({ value, children }: { value: SessionInfo; children: ReactNode }) {
  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): SessionInfo {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession fora de <SessionProvider>");
  return s;
}

/** Organização atual (as telas de módulo só existem com uma escolhida). */
export function useOrg(): OrgInfo {
  const s = useSession();
  if (!s.org) throw new Error("Nenhuma organização selecionada");
  return s.org;
}

export function useHasRole(min: Role): boolean {
  const s = useContext(SessionCtx);
  return (s?.org?.rank ?? 0) >= roleRank(min);
}

// ---------------------------------------------------------------- ações (mutações)

export interface ActionEnv {
  /** Abre o desafio de 2FA (ou o cadastro do fator) e resolve true quando a sessão virou aal2. */
  stepUp: (reason: string) => Promise<boolean>;
  notify: (tone: "ok" | "crit", title: string, body?: string) => void;
}

const ActionCtx = createContext<ActionEnv>({
  stepUp: async () => false,
  notify: () => undefined,
});

export function ActionProvider({ value, children }: { value: ActionEnv; children: ReactNode }) {
  return <ActionCtx.Provider value={value}>{children}</ActionCtx.Provider>;
}

export function useActionEnv(): ActionEnv {
  return useContext(ActionCtx);
}

/** Executa `fn`; se o banco pedir 2FA, abre o desafio e repete uma vez. */
export function useStepUpRunner() {
  const env = useActionEnv();
  return useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        return await fn();
      } catch (e) {
        const err = translateError(e);
        if (err.kind !== "aal2") throw err;
        const ok = await env.stepUp(err.message);
        if (!ok) throw err;
        return await fn();
      }
    },
    [env],
  );
}

export interface ActionOptions<R> {
  /** Chaves de cache a invalidar (prefixos). Convenção: começam pelo schema, ex. ["sales"]. */
  invalidate?: QueryKey[];
  success?: string | ((r: R) => string) | null;
  errorTitle?: string;
  onSuccess?: (r: R) => void;
  /** Não mostra toast de erro (a tela mostra o erro no próprio formulário). */
  silentError?: boolean;
}

/**
 * Mutação padrão do painel: escrita SÓ por RPC; 2FA sob demanda (detecta o erro do banco,
 * abre o desafio e repete); invalida o cache; aviso de sucesso e de erro em pt-BR.
 */
export function useAction<A, R>(fn: (args: A) => Promise<R>, opts: ActionOptions<R> = {}) {
  const qc = useQueryClient();
  const env = useActionEnv();
  const run = useStepUpRunner();
  return useMutation<R, VixioError, A>({
    mutationFn: (args: A) => run(() => fn(args)),
    onSuccess: async (r) => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      const msg = typeof opts.success === "function" ? opts.success(r) : opts.success;
      if (msg) env.notify("ok", msg);
      opts.onSuccess?.(r);
    },
    onError: (e) => {
      if (!opts.silentError) env.notify("crit", opts.errorTitle ?? "Não foi possível concluir", translateError(e).message);
    },
  });
}
